/**
 * The round after the lesson.
 *
 * The step-driven tutorial teaches the verbs, and it has to stay soft to do
 * that: a clock on a teaching step makes it fail at teaching, so the steps run
 * on halved durations against one ticket that cannot lapse and a fire somebody
 * lights for you. What that leaves untaught is the whole of what the game
 * actually feels like — a five-minute clock, tickets arriving on their own and
 * running out on their own, and a pan that catches light because you went to
 * fetch a plate. This is where a player meets those three for the first time,
 * on their own, instead of meeting them beside a stranger on a headset.
 *
 * It is not new game logic. It is `tutorialSandbox.ts` in `practice` mode —
 * same map, same recipes, same `tick` from `@overcooked/engine` — with the
 * shipped tuning in place of the learner's, and the step list switched off.
 * Everything below is what `Tutorial.tsx` already does for the sandbox (a
 * catch-up clock, the keys, and the bridge from engine events into an
 * `EventFeed`) plus the two things a round has that a lesson does not: the real
 * HUD, and a scoreboard at the end.
 *
 * The HUD is `Stage`, unchanged — the same component the round page and the
 * replay page mount, fed by the same `EventFeed`. That is deliberate to the
 * point of being the design: the clock, the ticket rail, the fuses, the score
 * badge and the recipe panel a player learns to read here are the identical
 * pixels they will be reading in the kitchen, because they are the identical
 * code.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { GameEvent, PlayerAction } from '@overcooked/shared'
import { KEYFRAME_INTERVAL_TICKS, TICK_MS } from '@overcooked/shared'
import { serialise } from '@overcooked/engine'
import { EventFeed } from '../feed/feed'
import type { RoundTally } from '../feed/feed'
import type { Catalog } from '../render/catalog'
import type { RecipesControl } from '../render/RecipePanel'
import { Stage } from '../render/Stage'
import { TUTORIAL_CONFIG } from './tutorialMap'
import type { Sandbox } from './tutorialSandbox'
import {
  SANDBOX_KEYS,
  SANDBOX_PLAYER_ID,
  advanceSandbox,
  createSandbox,
  practiceRoundStarted,
} from './tutorialSandbox'

/** No steps, and the same empty list every render, so the loop's deps hold still. */
const NO_STEPS: never[] = []

/**
 * The same cap `Tutorial.tsx` and `transport/loop.ts` use, for the same reason:
 * a tab that was in the background for a minute resumes rather than simulating
 * a minute of kitchen in one frame.
 */
const MAX_CATCH_UP_TICKS = 10

export interface PracticeRoundProps {
  catalog: Catalog
  /** Called when the player is done with it — finished, or asked to move on. */
  onDone(): void
}

interface Outcome {
  score: number
  tally: RoundTally
}

export function PracticeRound({ catalog, onDone }: PracticeRoundProps) {
  // One feed for the life of this component; `restart` resets it rather than
  // replacing it, so `Stage`'s subscription and renderer survive a second go.
  const [feed] = useState(() => new EventFeed())
  const [outcome, setOutcome] = useState<Outcome | null>(null)
  const [attempt, setAttempt] = useState(0)

  const sandboxRef = useRef<Sandbox | null>(null)
  const pendingRef = useRef<PlayerAction[]>([])

  /** The recipe panel, owned here because the key that toggles it is bound here. */
  const [recipesOpen, setRecipesOpen] = useState(false)
  const recipesOpenRef = useRef(false)
  recipesOpenRef.current = recipesOpen
  const recipes = useMemo<RecipesControl>(
    () => ({
      open: recipesOpen,
      pool: TUTORIAL_CONFIG.maps[0]?.orderPool ?? [],
      toggle: () => setRecipesOpen((open) => !open),
      close: () => setRecipesOpen(false),
    }),
    [recipesOpen],
  )

  const restart = useCallback(() => {
    setOutcome(null)
    setRecipesOpen(false)
    setAttempt((n) => n + 1)
  }, [])

  // The kitchen. Rebuilt on `attempt`, which is what "play it again" means: a
  // fresh round rather than a rewound one, exactly as the tutorial's own
  // Replay button builds a fresh sandbox rather than stepping back to zero.
  useEffect(() => {
    const sandbox = createSandbox(NO_STEPS, 0, 'practice')
    sandboxRef.current = sandbox
    pendingRef.current = []

    feed.reset()
    feed.setSelfId(SANDBOX_PLAYER_ID)
    // The whistle, then the room. `ROUND_STARTED` is what anchors the client's
    // tick clock to a wall-clock instant, so every bar, fuse and the round
    // clock itself are drawn against it rather than extrapolated from whichever
    // frame last happened to arrive.
    feed.push({
      tick: sandbox.state.tick,
      events: [
        practiceRoundStarted(sandbox.state, Date.now()),
        {
          tick: sandbox.state.tick,
          wallClock: Date.now(),
          actor: 'system',
          type: 'KEYFRAME',
          state: serialise(sandbox.state),
        },
      ],
    })

    let raf = 0
    let previous = performance.now()
    let owed = 0
    let sinceKeyframe = 0

    const frame = (): void => {
      const now = performance.now()
      owed += now - previous
      previous = now
      let due = Math.floor(owed / TICK_MS)
      if (due > MAX_CATCH_UP_TICKS) {
        owed = 0
        due = MAX_CATCH_UP_TICKS
      } else {
        owed -= due * TICK_MS
      }

      for (let i = 0; i < due; i += 1) {
        const before = sandboxRef.current
        if (!before || before.ended) break
        const actions = pendingRef.current
        pendingRef.current = []
        const after = advanceSandbox(before, NO_STEPS, actions)
        sandboxRef.current = after
        sinceKeyframe += 1
        const keyframe = sinceKeyframe >= KEYFRAME_INTERVAL_TICKS
        if (keyframe) sinceKeyframe = 0
        publish(feed, after, keyframe)
        if (after.ended) {
          // The scoreboard reads the feed's own tally rather than counting
          // serves here: it is the same number the HUD has been showing, and
          // two counts of one thing is one of them being wrong later.
          setOutcome({ score: after.ended.score, tally: feed.snapshot().tally })
          break
        }
      }

      raf = requestAnimationFrame(frame)
    }
    raf = requestAnimationFrame(frame)

    return () => {
      cancelAnimationFrame(raf)
      sandboxRef.current = null
    }
  }, [feed, attempt])

  // The keys, bound to `window` like every other screen's, and only while the
  // round is running — a scoreboard that still swallows the space bar is a
  // scoreboard whose buttons cannot be reached from the keyboard.
  useEffect(() => {
    if (outcome) return

    const onKeyDown = (event: KeyboardEvent): void => {
      const direction = SANDBOX_KEYS[event.key]
      if (direction) {
        event.preventDefault()
        if (recipesOpenRef.current) setRecipesOpen(false)
        pendingRef.current.push({ kind: 'move', direction })
        return
      }
      if (event.key === ' ') {
        event.preventDefault()
        if (!event.repeat) pendingRef.current.push({ kind: 'interact' })
        return
      }
      // `r` opens the recipes, here as in a round. The tutorial's last card
      // says so, and a card that teaches a key the practice round does not
      // answer to would be the lesson lying about the game.
      if (event.key === 'r' || event.key === 'R') {
        event.preventDefault()
        if (!event.repeat) setRecipesOpen((open) => !open)
        return
      }
      if (event.key === 'Escape' && recipesOpenRef.current) {
        event.preventDefault()
        setRecipesOpen(false)
      }
    }
    const onKeyUp = (event: KeyboardEvent): void => {
      if (event.key === ' ') pendingRef.current.push({ kind: 'interact_release' })
    }
    const onBlur = (): void => {
      pendingRef.current.push({ kind: 'interact_release' })
    }

    window.addEventListener('keydown', onKeyDown)
    window.addEventListener('keyup', onKeyUp)
    window.addEventListener('blur', onBlur)
    return () => {
      window.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('keyup', onKeyUp)
      window.removeEventListener('blur', onBlur)
    }
  }, [outcome])

  return (
    <section className="gate gate-practice">
      <p className="gate-step">Practice round</p>
      <h1>{outcome ? 'Round over' : 'Five minutes, just you'}</h1>
      <p className="hint">
        {outcome
          ? "That's the whole game. With a partner, you'll also need to talk."
          : 'Now the clock is real. Orders run out and food burns, so serve as many as you can.'}
      </p>

      {/* The kitchen exists while there is a round, exactly as on the room
          page: once the whistle has gone the number is the thing to look at,
          and a frozen kitchen underneath it is clutter. */}
      {!outcome && (
        <Stage
          feed={feed}
          catalog={catalog}
          selfId={SANDBOX_PLAYER_ID}
          showHud
          recipes={recipes}
          className="stage-practice"
        />
      )}

      {outcome ? (
        <>
          <p className="final-score">
            <span className="final-score-value">{outcome.score}</span>
            <span className="final-score-label">final score</span>
          </p>
          <ul className="round-summary">
            <li className="tally-good">
              <strong>{outcome.tally.matched}</strong> orders served
            </li>
            <li>
              <strong>{outcome.tally.served - outcome.tally.matched}</strong> dishes nobody ordered
            </li>
            <li className="tally-bad">
              <strong>{outcome.tally.expired}</strong> orders missed
            </li>
          </ul>
          <div className="gate-actions tutorial-actions">
            <button type="button" className="tutorial-next" onClick={onDone}>
              Done
            </button>
            <button type="button" onClick={restart}>
              Practice again
            </button>
          </div>
        </>
      ) : (
        <div className="gate-actions tutorial-actions">
          {/* Never a trap. Five minutes is a long time to have committed to by
              pressing one button, so the way out is on screen the whole time. */}
          <button type="button" className="tutorial-skip" onClick={onDone}>
            Skip practice
          </button>
        </div>
      )}
    </section>
  )
}

/**
 * One tick's events onto the feed, with a keyframe when one is due.
 *
 * The cadence is the server's — every tick's events, a full keyframe every
 * `KEYFRAME_INTERVAL_TICKS` — so the renderer sees the shape of traffic it sees
 * in a real round rather than a shape only the practice round produces.
 */
function publish(feed: EventFeed, sandbox: Sandbox, keyframe: boolean): void {
  const events: GameEvent[] = keyframe
    ? [
        {
          tick: sandbox.state.tick,
          wallClock: Date.now(),
          actor: 'system',
          type: 'KEYFRAME',
          state: serialise(sandbox.state),
        },
        ...sandbox.events,
      ]
    : sandbox.events
  if (events.length === 0) return
  feed.push({ tick: sandbox.state.tick, events })
}
